import { useEffect, useRef, useState } from "react";
import { Bot, CheckCircle, Loader2 } from "lucide-react";

import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import BlocoRecolhivel from "./BlocoRecolhivel";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("token") || ""}`, "Content-Type": "application/json" });
const SELECT = "w-full rounded-md border bg-background px-3 py-2 text-sm";

/**
 * CLEO: tipo de estabelecimento, tom de conversa, humor e as respostas de
 * perguntas frequentes (vagas, estacionamento, eventos). Era a aba Contexto
 * do ChatBot; veio pra cá a pedido do dono (29/09/2026). Lê e grava em
 * /api/chatbot/full-config e salva sozinho ~1 s depois da última mudança.
 */
export default function CleoPreferencias({ toast }) {
  const [secoes, setSecoes] = useState([]);
  const [valores, setValores] = useState(null);
  const [nicho, setNicho] = useState({ label: "", options: [] });
  const [status, setStatus] = useState("");
  const timer = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/chatbot/full-config`, { headers: auth(), cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setSecoes(d.sections || []);
        setValores(d.values || {});
        setNicho({ label: d.niche_detected_label || "", options: d.niche_options || [] });
      })
      .catch(() => {});
    return () => clearTimeout(timer.current);
  }, []);

  const mudar = (chave, valor) => {
    const novos = { ...valores, [chave]: valor };
    setValores(novos);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setStatus("salvando");
      try {
        const r = await fetch(`${API_URL}/api/chatbot/full-config`, { method: "PUT", headers: auth(), body: JSON.stringify({ values: novos }) });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Não foi possível salvar.");
        setStatus("salvo");
        setTimeout(() => setStatus(""), 1500);
      } catch (e) {
        setStatus("");
        toast?.({ title: "Erro ao salvar", description: e.message, variant: "destructive" });
      }
    }, 800);
  };

  const campo = (f) => {
    const dependentes = (f.depends_on || "").split(",").map((s) => s.trim()).filter(Boolean);
    if (!dependentes.every((k) => !!valores[k])) return null;
    const recuo = dependentes.length ? "ml-6 border-l-2 border-primary/20 pl-3" : "";
    if (f.type === "bool") {
      return (
        <div key={f.key} className={`flex items-center justify-between gap-3 ${recuo}`}>
          <div>
            <Label className="text-sm font-medium">{f.label}</Label>
            {f.hint && <p className="text-xs text-muted-foreground">{f.hint}</p>}
          </div>
          <Switch checked={!!valores[f.key]} onCheckedChange={(v) => mudar(f.key, v)} />
        </div>
      );
    }
    if (f.type === "select" || f.type === "select_niche") {
      const opcoes = f.type === "select_niche" ? nicho.options : f.options || [];
      return (
        <div key={f.key} className={`space-y-1.5 ${recuo}`}>
          <Label className="text-sm font-medium">{f.label}</Label>
          {f.type === "select_niche" && nicho.label && (
            <p className="rounded bg-blue-500/10 px-2 py-1 text-xs text-blue-700 dark:text-blue-300">
              Detectado pelo cardápio: <b>{nicho.label}</b>
            </p>
          )}
          <select value={valores[f.key] || (f.type === "select" ? f.default : "") || ""} onChange={(e) => mudar(f.key, e.target.value)} className={SELECT}>
            {f.type === "select_niche" && <option value="">Detectar automaticamente</option>}
            {opcoes.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {f.hint && <p className="text-xs text-muted-foreground">{f.hint}</p>}
        </div>
      );
    }
    return (
      <div key={f.key} className={`space-y-1.5 ${recuo}`}>
        <Label className="text-sm font-medium">{f.label}</Label>
        <Textarea value={valores[f.key] || ""} onChange={(e) => mudar(f.key, e.target.value)} placeholder={f.hint || ""}
          rows={f.rows || 3} maxLength={f.max_length} className="resize-none text-sm" />
      </div>
    );
  };

  return (
    // Bloco recolhível (29/09/2026): fechado, o conteúdo só fica escondido
    // (não desmonta), então o salvar-sozinho de 800 ms não perde nada.
    <BlocoRecolhivel e2e="card-cleo-preferencias" icone={Bot} titulo="CLEO: jeito de falar e perguntas frequentes"
      resumo="O ramo da loja, o tom da conversa e as respostas sobre vagas, estacionamento e eventos. Salva sozinho."
      controle={status === "salvando" ? <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Salvando…</span>
        : status === "salvo" ? <span className="flex items-center gap-1.5 text-xs text-green-500"><CheckCircle className="h-3 w-3" /> Salvo</span> : null}>
      <div className="space-y-5">
      {valores === null ? (
        <p className="text-sm text-muted-foreground">Carregando...</p>
      ) : secoes.map((s) => (
        <div key={s.id} className="space-y-3 border-t pt-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{s.title}</p>
          {s.fields.map(campo)}
        </div>
      ))}
      </div>
    </BlocoRecolhivel>
  );
}
